import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiCheck, FiMoreVertical, FiPlay, FiPause, FiSend } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { userAPI, notificationAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [limits, setLimits] = useState({ maxActiveUsers: 0, activeUsersCount: 0 });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    take: 10,
    itemCount: 0,
    pageCount: 0,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await userAPI.getUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to fetch users.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLimits = useCallback(async () => {
    try {
      const { data } = await userAPI.getUserLimits();
      setLimits(data);
    } catch (error) {
      toast.error('Failed to fetch user limits.');
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchLimits();
  }, [fetchUsers, fetchLimits]);

  const handleAction = async (action, userId) => {
    try {
      switch (action) {
        case 'approve':
          await userAPI.approveUser(userId);
          toast.success('User approved successfully.');
          break;
        case 'activate':
          await userAPI.activateUser(userId);
          toast.success('User activated successfully.');
          break;
        case 'deactivate':
          await userAPI.deactivateUser(userId);
          toast.success('User deactivated successfully.');
          break;
        case 'test-notification':
          const message = prompt('Enter notification message:');
          if (message) {
            await notificationAPI.sendTestNotification({ userId, message });
            toast.success('Test notification sent.');
          }
          break;
        default:
          break;
      }
      fetchUsers();
      fetchLimits();
    } catch (error) {
      toast.error(`Failed to ${action} user.`);
      console.error(error);
    }
  };

  useEffect(() => {
    if (users.length > 0) {
      setPagination(prev => ({
        ...prev,
        itemCount: users.length,
        pageCount: Math.ceil(users.length / prev.take),
      }));
    }
  }, [users]);

  const StatusBadge = ({ status }) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const Pagination = () => {
    if (!pagination) {
      return null;
    }

    return (
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-gray-700">
          Page {pagination.page} of {pagination.pageCount}
        </span>
        <div className="flex space-x-2">
          <button
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            disabled={pagination.page <= 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            disabled={pagination.page >= pagination.pageCount}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">จัดการผู้ใช้ในระบบ</h1>
        <p className="text-gray-600">
          จัดการบัญชีผู้ใช้และสิทธิ์การใช้งาน จำนวนผู้ใช้ที่ใช้งานอยู่: {limits.activeUsersCount} / {limits.maxActiveUsers}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users
                    .slice((pagination.page - 1) * pagination.take, pagination.page * pagination.take)
                    .map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {user.pictureUrl ? (
                              <img 
                                className="h-10 w-10 rounded-full object-cover" 
                                src={user.pictureUrl} 
                                alt={user.displayName}
                                onError={(e) => {
                                  e.target.onerror = null; 
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }} 
                              />
                            ) : null}
                            <div 
                              className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center"
                              style={{ display: user.pictureUrl ? 'none' : 'flex' }}
                            >
                              <SafeIcon icon={FiUsers} className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
                              <div className="text-sm text-gray-500">{user.lineUserId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={user.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {user.displayName !== 'System Manager' && user.displayName !== 'System Administrator' && (
                            <div className="relative inline-block text-left">
                              <div className="group">
                                <button type="button" className="inline-flex items-center justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
                                  Actions
                                  <FiMoreVertical className="ml-2 -mr-1 h-5 w-5" />
                                </button>
                                <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                  <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                                    {user.status !== 'approved' && (
                                      <button onClick={() => handleAction('approve', user.id)} className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                                        <FiCheck className="mr-3 h-5 w-5 text-green-500" />
                                        <span>Approve</span>
                                      </button>
                                    )}
                                    {user.isActive ? (
                                      <button onClick={() => handleAction('deactivate', user.id)} className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                                        <FiPause className="mr-3 h-5 w-5 text-yellow-500" />
                                        <span>Deactivate</span>
                                      </button>
                                    ) : (
                                      <button onClick={() => handleAction('activate', user.id)} className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                                        <FiPlay className="mr-3 h-5 w-5 text-green-500" />
                                        <span>Activate</span>
                                      </button>
                                    )}
                                    <button onClick={() => handleAction('test-notification', user.id)} className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                                      <FiSend className="mr-3 h-5 w-5 text-blue-500" />
                                      <span>Test Notification</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <Pagination />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
